import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { FormLayout } from '@astryxdesign/core/FormLayout';
import { MetadataList, MetadataListItem } from '@astryxdesign/core/MetadataList';
import { Text } from '@astryxdesign/core/Text';
import { TextArea } from '@astryxdesign/core/TextArea';
import { TextInput } from '@astryxdesign/core/TextInput';
import { VStack } from '@astryxdesign/core/VStack';
import { useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';
import type { Studio, StudioService } from '@/api/schemas';
import { errorCode, errorText } from '@/lib/errors';
import { fmtDateTime, fmtDuration, fmtMoney } from '@/lib/format';
import { useInputAttrs } from '@/lib/useInputAttrs';

export const contactSchema = z.object({
  name: z.string().trim().min(2, 'Как к вам обращаться?').max(80, 'Слишком длинное имя'),
  phone: z
    .string()
    .trim()
    .refine((v) => {
      const d = v.replace(/\D/g, '');
      return d.length >= 10 && d.length <= 15;
    }, 'Номер в формате +7 900 123-45-67'),
  car: z.string().trim().min(2, 'Марка и модель, например «Kia Rio»').max(120),
  comment: z.string().trim().max(500, 'До 500 символов'),
});
export type ContactForm = z.infer<typeof contactSchema>;

const DRAFT_KEY = 'booking-contact-draft';
const loadDraft = (): ContactForm => {
  try {
    return { name: '', phone: '', car: '', comment: '', ...JSON.parse(sessionStorage.getItem(DRAFT_KEY) ?? '{}') };
  } catch {
    return { name: '', phone: '', car: '', comment: '' };
  }
};

export function ContactsStep({
  studio,
  service,
  startsAt,
  submitting,
  error,
  onSubmit,
}: {
  studio: Studio;
  service: StudioService;
  startsAt: string;
  submitting: boolean;
  error: unknown;
  onSubmit: (form: ContactForm) => void;
}) {
  const [form, setForm] = useState<ContactForm>(loadDraft);
  const [issues, setIssues] = useState<Partial<Record<keyof ContactForm, string>>>({});
  const phoneRef = useInputAttrs<HTMLDivElement>({ inputmode: 'tel', enterkeyhint: 'next' });
  const nameRef = useInputAttrs<HTMLDivElement>({ enterkeyhint: 'next', autocapitalize: 'words' });
  const carRef = useInputAttrs<HTMLDivElement>({ enterkeyhint: 'done' });

  // Keep what was typed if the person steps back to change the time.
  useEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    } catch {
      /* ignore */
    }
  }, [form]);

  const set = (k: keyof ContactForm) => (v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (issues[k]) setIssues((i) => ({ ...i, [k]: undefined }));
  };

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const parsed = contactSchema.safeParse(form);
    if (!parsed.success) {
      const next: typeof issues = {};
      for (const i of parsed.error.issues) next[i.path[0] as keyof ContactForm] ??= i.message;
      setIssues(next);
      const first = Object.keys(next)[0];
      document.querySelector<HTMLInputElement>(`[data-field="${first}"] input, [data-field="${first}"] textarea`)?.focus();
      return;
    }
    onSubmit(parsed.data);
  };

  const status = (k: keyof ContactForm) => (issues[k] ? { type: 'error' as const, message: issues[k] } : undefined);
  const code = error ? errorCode(error) : null;

  return (
    <form onSubmit={submit} noValidate>
      <VStack gap={5}>
        <MetadataList>
          <MetadataListItem label="Услуга">{service.name}</MetadataListItem>
          <MetadataListItem label="Когда">{fmtDateTime(startsAt, studio.timezone)}</MetadataListItem>
          <MetadataListItem label="Длительность">{fmtDuration(service.durationMin)}</MetadataListItem>
          <MetadataListItem label="Стоимость">{fmtMoney(service.priceCents, studio.currency, service.priceIsFrom)}</MetadataListItem>
        </MetadataList>

        {code && code !== 'slot_taken' && <Banner status="error" title={errorText(error)} collapsible={false} />}

        <FormLayout defaultOptionality="required">
          <div data-field="name" ref={nameRef}>
            <TextInput label="Имя" value={form.name} onChange={set('name')} autoComplete="name" status={status('name')} statusVariant="detached" width="100%" />
          </div>
          <div data-field="phone" ref={phoneRef}>
            <TextInput
              label="Телефон"
              value={form.phone}
              onChange={set('phone')}
              autoComplete="tel"
              placeholder="+7 900 123-45-67"
              status={status('phone')}
              statusVariant="detached"
              width="100%"
            />
          </div>
          <div data-field="car" ref={carRef}>
            <TextInput
              label="Автомобиль"
              value={form.car}
              onChange={set('car')}
              placeholder="Марка, модель, цвет"
              status={status('car')}
              statusVariant="detached"
              width="100%"
            />
          </div>
          <div data-field="comment">
            <TextArea label="Комментарий" isOptional value={form.comment} onChange={set('comment')} rows={2} maxLength={500} width="100%" />
          </div>
        </FormLayout>

        {studio.settings.bookingRules && <Text type="supporting">{studio.settings.bookingRules}</Text>}
        <Text type="supporting">Регистрация не нужна: ссылка на запись сохранится на этом устройстве.</Text>

        <Button type="submit" label="Подтвердить запись" variant="primary" size="lg" width="100%" isLoading={submitting} />
      </VStack>
    </form>
  );
}

export function clearContactDraft() {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* ignore */
  }
}
